import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import { Sort, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import TableLoader from '../components/TableLoader';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiSearch,
} from 'react-icons/fi';
import { donationsAPI } from '../services/api';
import { colors } from '../theme/modernTheme';
import { useMultiSelect } from '../hooks/useMultiSelect';
import BulkActionToolbar from '../components/BulkActionToolbar';
import BulkDeleteDialog from '../components/BulkDeleteDialog';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { showSuccess, showError } from '../utils/toast';
import { formatDateForDisplay } from '../utils/dateUtils';
import { adjustPageAfterDeletion } from '../utils/paginationUtils';
import { getFrequencyLabel } from '../utils/frequencyLabel';
import { selectDonationSchemas } from '../store/slices/donationSchemasSlice';

export default function Donations() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [donations, setDonations] = useState([]);

  // Get donation schemas from Redux
  const donationSchemas = useSelector(selectDonationSchemas);

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [donationToDelete, setDonationToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [orderBy, setOrderBy] = useState('pledge_start_date');
  const [order, setOrder] = useState('desc');

  // Temporary filter states (not applied until user clicks Apply)
  const [donorSearchInput, setDonorSearchInput] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [donationSchemaInput, setDonationSchemaInput] = useState([]);
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');

  // Applied filter states (used for API calls)
  const [donorSearch, setDonorSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [donationSchemaFilter, setDonationSchemaFilter] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Multi-select state
  const multiSelect = useMultiSelect(donations, totalCount, 'id');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Initialize filters from URL parameters BEFORE first load
  useEffect(() => {
    const statusParam = searchParams.get('status');

    if (statusParam) {
      setStatusInput(statusParam);
      setStatusFilter(statusParam);
    }
  }, []);

  useEffect(() => {
    loadDonations();
  }, [
    page,
    rowsPerPage,
    donorSearch,
    statusFilter,
    donationSchemaFilter,
    startDate,
    endDate,
    orderBy,
    order,
  ]);

  const loadDonations = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortOrder: order,
      };

      if (donorSearch) params.donor_search = donorSearch;

      // Handle status filter
      if (statusFilter) {
        params.pledge_status = statusFilter;
      }

      if (donationSchemaFilter.length > 0) {
        params.donation_schema = donationSchemaFilter.map((s) => s.id).join(',');
      }

      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await donationsAPI.getAll(params);
      setDonations(response.data);
      setTotalCount(response.pagination?.total || response.data.length);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate('/donations/new');
  };

  const handleEdit = (donation) => {
    navigate(`/donations/${donation.id}?mode=edit`);
  };

  const handleRowClick = (donation) => {
    navigate(`/donations/${donation.id}`);
  };

  const handleDeleteClick = (donation) => {
    setDonationToDelete(donation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await donationsAPI.delete(donationToDelete.id);
      multiSelect.removeFromSelection(donationToDelete.id);
      setDeleteDialogOpen(false);
      setDonationToDelete(null);

      // Adjust page if needed after deletion
      const newTotalCount = totalCount - 1;
      adjustPageAfterDeletion(page, newTotalCount, rowsPerPage, setPage);

      loadDonations();
      showSuccess('Pledge deleted successfully. Associated transactions have been preserved.');
    } catch (error) {
      showError(error?.error?.message || 'Failed to delete pledge');
    } finally {
      setDeleting(false);
    }
  };

  const handleSuccess = () => {
    loadDonations();
  };

  const handleSendReceipt = async (donation) => {
    try {
      await donationsAPI.sendReceipt(donation.id);
      showSuccess('Receipt sent successfully!');
    } catch (error) {
      const errorMessage = error?.error?.message || 'Failed to send receipt';
      showError(errorMessage);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleApplyFilters = () => {
    setDonorSearch(donorSearchInput);
    setStatusFilter(statusInput);
    setDonationSchemaFilter(donationSchemaInput);
    setStartDate(startDateInput);
    setEndDate(endDateInput);
    setPage(0);
    multiSelect.clearSelection();
  };

  const handleClearFilters = () => {
    setDonorSearchInput('');
    setStatusInput('');
    setDonationSchemaInput([]);
    setStartDateInput('');
    setEndDateInput('');
    setDonorSearch('');
    setStatusFilter('');
    setDonationSchemaFilter([]);
    setStartDate('');
    setEndDate('');
    setPage(0);
    multiSelect.clearSelection();
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  const hasActiveFilters =
    donorSearchInput ||
    statusInput ||
    donationSchemaInput.length > 0 ||
    startDateInput ||
    endDateInput;

  // Check if filters have changed from applied state
  const hasFilterChanges =
    donorSearchInput !== donorSearch ||
    statusInput !== statusFilter ||
    JSON.stringify(donationSchemaInput) !== JSON.stringify(donationSchemaFilter) ||
    startDateInput !== startDate ||
    endDateInput !== endDate;

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const selectionData = multiSelect.getSelectionData();
      const deleteCount = multiSelect.selectedCount;

      const filters = {};
      if (donorSearch) filters.donor_search = donorSearch;

      // Handle status filter
      if (statusFilter) {
        filters.pledge_status = statusFilter;
      }

      if (donationSchemaFilter.length > 0) {
        filters.donation_schema = donationSchemaFilter.map((s) => s.id).join(',');
      }

      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      await donationsAPI.bulkDelete({
        ...selectionData,
        filters,
      });

      setBulkDeleteDialogOpen(false);
      multiSelect.clearSelection();

      // Adjust page if needed after bulk deletion
      const newTotalCount = totalCount - deleteCount;
      adjustPageAfterDeletion(page, newTotalCount, rowsPerPage, setPage);

      loadDonations();
      showSuccess(`Successfully deleted ${deleteCount} donation(s)`);
    } catch (error) {
      showError(error?.error?.message || 'Failed to delete donations');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Donations are now sorted server-side, no need for client-side sorting
  const sortedDonations = donations;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} mt={1}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.text.primary,
          }}
        >
          Pledges
        </Typography>
        <Button
          variant="contained"
          startIcon={<FiPlus />}
          onClick={handleAdd}
          sx={{
            borderRadius: '4px',
            backgroundColor: colors.primary.main,
            '&:hover': { backgroundColor: colors.primary.dark },
          }}
        >
          Add Pledge
        </Button>
      </Box>

      {/* Filters and Pagination */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '4px',
          opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
          pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search donor name"
              value={donorSearchInput}
              onKeyPress={handleFilterKeyPress}
              disabled={multiSelect.selectedCount > 0}
              autoComplete="off"
              InputProps={{
                endAdornment: donorSearchInput ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDonorSearchInput('');
                      setDonorSearch('');
                      setPage(0);
                      multiSelect.clearSelection();
                    }}
                    disabled={multiSelect.selectedCount > 0}
                    sx={{ padding: '4px' }}
                  >
                    <FiX size={16} style={{ color: colors.text.secondary }} />
                  </IconButton>
                ) : (
                  <FiSearch style={{ marginRight: 8, color: colors.text.secondary }} />
                ),
              }}
              onChange={(e) => setDonorSearchInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1.2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusInput}
              disabled={multiSelect.selectedCount > 0}
              SelectProps={{
                MenuProps: {
                  sx: {
                    paddingLeft: 0,
                  },
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                    },
                  },
                },
              }}
              onChange={(e) => setStatusInput(e.target.value)}
            >
              <MenuItem value="" sx={{ p: '2px 8px', minHeight: '32px' }}>
                All Statuses
              </MenuItem>
              <MenuItem value="PENDING" sx={{ p: '2px 8px', minHeight: '32px' }}>
                Pending
              </MenuItem>
              <MenuItem value="PAUSED" sx={{ p: '2px 8px', minHeight: '32px' }}>
                Paused
              </MenuItem>
              <MenuItem value="CLOSED" sx={{ p: '2px 8px', minHeight: '32px' }}>
                Closed
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={donationSchemas}
              getOptionLabel={(option) => option.name}
              value={donationSchemaInput}
              onChange={(event, newValue) => {
                setDonationSchemaInput(newValue);
                // Auto-apply when clearing all donation schemes (clicking X to clear all)
                if (newValue.length === 0 && donationSchemaInput.length > 0) {
                  setTimeout(() => {
                    setDonationSchemaFilter([]);
                    setPage(0);
                  }, 0);
                }
              }}
              disabled={multiSelect.selectedCount > 0}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              forcePopupIcon={true}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: '4px',
                    maxHeight: '300px',
                    '& .MuiAutocomplete-listbox': {
                      padding: '4px',
                      maxHeight: '300px',
                    },
                  },
                },
              }}
              componentsProps={{
                popper: {
                  placement: 'bottom-start',
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: false,
                    },
                  ],
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Donation Schemes"
                  placeholder={donationSchemaInput.length === 0 ? 'All Schemes' : ''}
                  inputProps={{
                    ...params.inputProps,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: { fontSize: '0.875rem' },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) => {
                const numTags = value.length;
                const limitTags = 1;

                return (
                  <>
                    {value.slice(0, limitTags).map((option, index) => {
                      const { key, ...chipProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          {...chipProps}
                          label={option.name}
                          size="small"
                          sx={{
                            borderRadius: '4px',
                            height: '20px',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            backgroundColor: colors.primary.main,
                            color: '#fff',
                            maxWidth: '120px',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                            '& .MuiChip-deleteIcon': {
                              color: 'rgba(255, 255, 255, 0.7)',
                              '&:hover': {
                                color: '#fff',
                              },
                            },
                          }}
                        />
                      );
                    })}
                    {numTags > limitTags && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '18px',
                          px: 1.5,
                          borderRadius: '4px',
                          backgroundColor: `${colors.primary.main}15`,
                          color: colors.primary.main,
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          ml: 0.5,
                        }}
                      >
                        +{numTags - limitTags}
                      </Box>
                    )}
                  </>
                );
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{
                      display: 'flex !important',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      minHeight: '40px',
                      backgroundColor: 'transparent !important',
                      borderRadius: '4px !important',
                      '&:hover': {
                        backgroundColor: `${colors.action.hover} !important`,
                        borderRadius: '4px !important',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        border: `2px solid ${selected ? colors.primary.main : '#64748b'}`,
                        borderRadius: '3px',
                        backgroundColor: selected ? colors.primary.main : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {selected && (
                        <Box component="svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </Box>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: colors.text.primary,
                      }}
                    >
                      {option.name}
                    </Typography>
                  </Box>
                );
              }}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date From"
              value={startDateInput}
              onChange={(e) => setStartDateInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              InputLabelProps={{ shrink: true }}
              onClick={(e) => e.target.showPicker?.()}
              sx={{ cursor: 'pointer' }}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date To"
              value={endDateInput}
              onChange={(e) => setEndDateInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              InputLabelProps={{ shrink: true }}
              onClick={(e) => e.target.showPicker?.()}
              sx={{ cursor: 'pointer' }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                onClick={handleApplyFilters}
                disabled={!hasFilterChanges || multiSelect.selectedCount > 0}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  mr: -1,
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Apply
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters || multiSelect.selectedCount > 0}
                sx={{
                  mr: -1,
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Clear
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} md={1.8}>
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
              <IconButton
                size="small"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0 || multiSelect.selectedCount > 0}
                sx={{
                  borderRadius: 1,
                  marginRight: -2,
                  '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  },
                }}
              >
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: '100px',
                  textAlign: 'center',
                }}
              >
                Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setPage(page + 1)}
                disabled={
                  page >= Math.ceil(totalCount / rowsPerPage) - 1 || multiSelect.selectedCount > 0
                }
                sx={{
                  marginLeft: -2,
                  borderRadius: 1,
                  '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  },
                }}
              >
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={multiSelect.selectedCount}
        onClearSelection={multiSelect.clearSelection}
        onDelete={() => setBulkDeleteDialogOpen(true)}
        canDelete={true}
        itemName="donations"
      />

      {loading ? (
        <TableLoader message="Loading donations..." />
      ) : sortedDonations.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: colors.text.secondary,
          }}
        >
          <Typography variant="body1">No donations found</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: '4px',
            boxShadow: 'none',
            border: `1px solid ${colors.border.main}`,
          }}
        >
          <Table>
            <TableHead sx={{ borderBottom: '1px solid #E5E7EB' }}>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: '2%', py: 1, px: 1 }}>
                  <Checkbox
                    size="medium"
                    indeterminate={multiSelect.isSomeCurrentPageSelected}
                    checked={multiSelect.isAllCurrentPageSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      // Only select donations that can be deleted
                      const deletableDonations = donations.filter((d) => d.can_delete !== false);
                      if (multiSelect.isAllCurrentPageSelected) {
                        multiSelect.clearSelection();
                      } else {
                        deletableDonations.forEach((donation) => {
                          if (!multiSelect.isSelected(donation)) {
                            multiSelect.toggleSelect(donation);
                          }
                        });
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    sx={{
                      padding: '4px',
                      color: colors.text.secondary,
                      '& .MuiSvgIcon-root': { fontSize: 28 },
                      transform: 'scale(1.2)',
                      '&.Mui-checked': {
                        color: colors.primary.main,
                      },
                      '&.MuiCheckbox-indeterminate': {
                        color: colors.primary.main,
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    paddingLeft: 0,
                    fontWeight: 600,
                    color:
                      multiSelect.selectedCount > 0 ? colors.text.disabled : colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: multiSelect.selectedCount > 0 ? 'not-allowed' : 'pointer',
                    userSelect: 'none',
                    pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => handleSort('pledge_start_date')}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Start Date
                    {orderBy === 'pledge_start_date' ? (
                      order === 'asc' ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <Sort fontSize="small" sx={{ opacity: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '200px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Donor
                </TableCell>
                <TableCell
                  sx={{
                    width: '120px',
                    fontWeight: 600,
                    color:
                      multiSelect.selectedCount > 0 ? colors.text.disabled : colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: multiSelect.selectedCount > 0 ? 'not-allowed' : 'pointer',
                    userSelect: 'none',
                    pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => handleSort('amount')}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Total Amount
                    {orderBy === 'amount' ? (
                      order === 'asc' ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <Sort fontSize="small" sx={{ opacity: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '120px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Collected Amount
                </TableCell>
                <TableCell
                  sx={{
                    width: '100px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Pledge Status
                </TableCell>
                <TableCell
                  sx={{
                    width: '100px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Donation Scheme
                </TableCell>
                <TableCell
                  sx={{
                    width: '80px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {multiSelect.selectedCount > 0 ? '' : 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedDonations.map((donation) => (
                <TableRow
                  key={donation.id}
                  onClick={() => handleRowClick(donation)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: multiSelect.isSelected(donation)
                      ? colors.primary.main + '10'
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: multiSelect.isSelected(donation)
                        ? colors.primary.main + '20'
                        : colors.action.row,
                    },
                    '&:last-child td, &:last-child th': { border: 0 },
                    borderBottom: `1px solid ${colors.border.main}`,
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    sx={{ py: 1.5, px: 1 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip
                      title={
                        donation.can_delete === false
                          ? `Cannot delete pledge with collected funds (₹${donation.collected_amount.toLocaleString()}). Please delete all transactions first.`
                          : ''
                      }
                      arrow
                      placement="top"
                    >
                      <span
                        style={{
                          cursor: donation.can_delete === false ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <Checkbox
                          size="medium"
                          disabled={donation.can_delete === false}
                          checked={multiSelect.isSelected(donation)}
                          onChange={(e) => {
                            e.stopPropagation();
                            multiSelect.toggleSelect(donation);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          sx={{
                            padding: '4px',
                            '& .MuiSvgIcon-root': { fontSize: 28 },
                            transform: 'scale(1.2)',
                            color:
                              donation.can_delete === false
                                ? colors.text.disabled
                                : colors.text.secondary,
                            '&.Mui-checked': {
                              color: colors.primary.main,
                            },
                            '&.Mui-disabled': {
                              color: colors.text.disabled,
                            },
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      paddingLeft: 0,
                      width: '150px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    {donation.pledge_start_date && donation.pledge_start_date !== 'Invalid Date'
                      ? formatDateForDisplay(donation.pledge_start_date)
                      : 'Invalid Date'}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: '160px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Tooltip title={donation.donor_name || '—'} arrow placement="top">
                        <span>{donation.donor_name || '—'}</span>
                      </Tooltip>
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '120px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    {donation.currency === 'INR' ? '₹' : '$'}
                    {parseFloat(donation.total_pledge_amount || donation.amount).toLocaleString()}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '120px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    {donation.currency === 'INR' ? '₹' : '$'}
                    {parseFloat(donation.collected_amount || 0).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ py: 1, maxWidth: '100px' }}>
                    <Chip
                      label={donation.pledge_status || 'PENDING'}
                      size="small"
                      sx={{
                        backgroundColor:
                          donation?.pledge_status === 'PENDING'
                            ? `#E3F2FD`
                            : donation?.pledge_status === 'PAUSED'
                              ? `#FEF3C7`
                              : donation?.pledge_status === 'CLOSED'
                                ? `${colors.text.disabled}15`
                                : colors.action.hover,
                        color:
                          donation?.pledge_status === 'PENDING'
                            ? '#1976D2'
                            : donation?.pledge_status === 'PAUSED'
                              ? '#92400E'
                              : donation?.pledge_status === 'CLOSED'
                                ? colors.text.disabled
                                : colors.text.secondary,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        borderRadius: '4px',
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '120px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    {donation?.donation_schema_name || '-'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ py: 1 }}>
                    {multiSelect.selectedCount > 0 ? (
                      <Box sx={{ width: '72px' }} />
                    ) : (
                      <Box display="flex" gap={0.5}>
                        {/* <Tooltip title="Send Receipt">
                          <IconButton
                            size="small"
                            onClick={() => handleSendReceipt(donation)}
                            sx={{
                              color: colors.text.primary,
                              p: 1,
                              "&:hover": {
                                backgroundColor: "transparent",
                                color: colors.primary.main,
                              },
                            }}>
                            <FiFileText size={16} />
                          </IconButton>
                        </Tooltip> */}
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(donation)}
                            sx={{
                              color: colors.text.primary,
                              p: 1,
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: colors.primary.main,
                              },
                            }}
                          >
                            <FiEdit2 size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            donation.can_delete === false
                              ? `Cannot delete pledge with collected funds (₹${donation.collected_amount.toLocaleString()}). Please delete all transactions first.`
                              : 'Delete'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={donation.can_delete === false}
                              onClick={() => handleDeleteClick(donation)}
                              sx={{
                                color:
                                  donation.can_delete === false
                                    ? colors.text.disabled
                                    : colors.text.primary,
                                p: 1,
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color:
                                    donation.can_delete === false
                                      ? colors.text.disabled
                                      : colors.error.main,
                                },
                                '&.Mui-disabled': {
                                  color: colors.text.disabled,
                                },
                              }}
                            >
                              <FiTrash2 size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Bottom Pagination */}
      {!loading && sortedDonations.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 2,
            gap: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            sx={{
              marginRight: -2,
              borderRadius: 1,

              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <FiChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              minWidth: '100px',
              textAlign: 'center',
            }}
          >
            Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPage(page + 1)}
            disabled={page >= Math.ceil(totalCount / rowsPerPage) - 1}
            sx={{
              marginLeft: -2,
              borderRadius: 1,

              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <FiChevronRight />
          </IconButton>
        </Box>
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="pledge"
        deleting={deleting}
      >
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, px: 2, textAlign: 'center' }}
        >
          This action cannot be undone. The pledge and any associated reminders will be permanently
          deleted. Associated transactions will be preserved.
        </Typography>
      </DeleteConfirmationDialog>

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        count={multiSelect.selectedCount}
        itemName="pledges"
        deleting={bulkDeleting}
      >
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, px: 2, textAlign: 'center' }}
        >
          This action cannot be undone. All selected pledges, their associated reminders and
          transactions will be permanently deleted.
        </Typography>
      </BulkDeleteDialog>
    </Box>
  );
}
